import React from 'react';
import { useTranslation } from 'react-i18next';
import { getSupportedRatios } from '../../../services/imageGenerationApi';

interface VideoOptionsGridProps {
  videoDuration: number;
  setVideoDuration: (duration: number) => void;
  videoQuality: string;
  setVideoQuality: (quality: string) => void;
  videoAspectRatio: string;
  setVideoAspectRatio: (ratio: string) => void;
  selectedVideoProvider: 'runway' | 'ltx' | 'veo';
  setSelectedVideoProvider: (provider: 'runway' | 'ltx' | 'veo') => void;
  isAdmin: boolean;
}

// Video duration options - varies by provider
const getDurationOptions = (provider: string) => {
  switch (provider) {
    case 'runway':
      return [
        // only temps, we are using veo to default generation on the backend
        //{ name: '8 seconds (Fixed)', value: 8 }
        { name: '5 seconds', value: 5 },
        { name: '10 seconds', value: 10 }
      ];
    case 'ltx':
      return [
        { name: '5 seconds', value: 5 },
        { name: '10 seconds', value: 10 }
      ];
    case 'veo':
      return [
        { name: '8 seconds (Fixed)', value: 8 }
      ];
    default:
      return [
        // only temps, we are using veo to default generation on the backend
        //{ name: '8 seconds (Fixed)', value: 8 }
        { name: '3 seconds', value: 3 },
        { name: '5 seconds', value: 5 }
      ];
  }
};

// Aspect ratio options - varies by provider  
const getAspectRatioOptions = (provider: string) => {
  switch (provider) {
    case 'veo':
      return [
        { name: '16:9 (Landscape)', value: '16:9' },
        { name: '9:16 (Portrait)', value: '9:16' }
      ];
    case 'runway':
    case 'ltx':
    default:
      return getSupportedRatios('runway');
  }
};

// Video quality options
const VIDEO_QUALITIES = [
  { name: 'Standard', value: 'standard' },
  { name: 'High', value: 'high' },
  { name: 'Ultra', value: 'ultra' }
];

const VideoOptionsGrid: React.FC<VideoOptionsGridProps> = ({
  videoDuration,
  setVideoDuration,
  videoQuality,
  setVideoQuality,
  videoAspectRatio,
  setVideoAspectRatio,
  selectedVideoProvider,
  setSelectedVideoProvider,
  isAdmin,
}) => {
  const { t } = useTranslation('aiGenerator');
  const durationOptions = getDurationOptions(selectedVideoProvider);
  const aspectRatioOptions = getAspectRatioOptions(selectedVideoProvider);

  return (
    <div style={{ 
      display: 'grid', 
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
      gap: '20px', 
      marginBottom: '20px' 
    }}>
      {/* Duration */}
      <div>
        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
          {t('video.duration')}
        </label>
        <select
          value={videoDuration}
          onChange={(e) => setVideoDuration(Number(e.target.value))}
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '6px',
            backgroundColor: '#1a1a1a',
            color: 'white',
            fontSize: '14px',
            outline: 'none'
          }}
        >
          {durationOptions.map((duration, index) => (
            <option key={index} value={duration.value}>
              {t('video.durationSeconds', { count: duration.value })}
            </option>
          ))}
        </select>
        {selectedVideoProvider === 'veo' && (
          <p style={{ 
            margin: '4px 0 0 0', 
            fontSize: '11px', 
            color: '#10b981',
            fontWeight: '500'
          }}>
            🎵 Includes native audio
          </p>
        )}
      </div>

      {/* Quality */}
      <div>
        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
          {t('video.quality')}
        </label>
        <select
          value={videoQuality}
          onChange={(e) => setVideoQuality(e.target.value)}
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '6px',
            backgroundColor: '#1a1a1a',
            color: 'white',
            fontSize: '14px',
            outline: 'none'
          }}
        >
          {VIDEO_QUALITIES.map((quality, index) => (
            <option key={index} value={quality.value}>
              {t(`video.quality${quality.value.charAt(0).toUpperCase() + quality.value.slice(1)}`)}
            </option>
          ))}
        </select>
      </div>

      {/* Aspect Ratio */}
      <div>
        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
          {t('video.aspectRatio')}
        </label>
        <select
          value={videoAspectRatio}
          onChange={(e) => setVideoAspectRatio(e.target.value)}
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '6px',
            backgroundColor: '#1a1a1a',
            color: 'white',
            fontSize: '14px',
            outline: 'none'
          }}
        >
          {aspectRatioOptions.map((ratio, index) => (
            <option key={index} value={ratio.value}>
              {/* Use translation if available, otherwise fall back to ratio name */}
              {ratio.value === '16:9' ? t('video.aspectRatioLandscape') :
               ratio.value === '9:16' ? t('video.aspectRatioPortrait') :
               ratio.value === '1:1' ? t('video.aspectRatioSquare') :
               ratio.name}
            </option>
          ))}
        </select>
      </div>

      {/* Provider Selection (Video) */}
      {isAdmin && (
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
            {t('video.provider')}
          </label>
          <select
            value={selectedVideoProvider}
            onChange={(e) => setSelectedVideoProvider(e.target.value as 'runway' | 'ltx' | 'veo')}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '6px',
              backgroundColor: '#1a1a1a',
              color: 'white',
              fontSize: '14px',
              outline: 'none'
            }}
          >
            <option value="runway">{t('video.providerRunway')}</option>
            <option value="ltx">{t('video.providerLtx')}</option>
            <option value="veo">{t('video.providerVeo')}</option>
          </select>
        </div>
      )}
    </div>
  );
};

export default VideoOptionsGrid;